import qz from "qz-tray";
import { buildEscPosReceipt, ReceiptOrder } from "./receiptPrint";

const getPreferredPrinter = async () => {
  const envName = process.env.REACT_APP_QZ_PRINTER;
  if (envName) return envName;

  const printers = await qz.printers.find();
  if (!printers || printers.length === 0) {
    throw new Error("No printers found");
  }
  return printers[0];
};

const withTimeout = async <T>(
  promise: Promise<T>,
  ms: number,
  message: string,
) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const ensureConnected = async () => {
  if (qz.websocket.isActive()) return;
  await withTimeout(qz.websocket.connect(), 4000, "QZ Tray not connected");
};

export const printReceiptQz = async (
  order: ReceiptOrder,
  customerName?: string,
  logoBase64?: string,
  currencySymbol = "$",
) => {
  await ensureConnected();
  const printerName = await getPreferredPrinter();
  const config = qz.configs.create(printerName, {
    encoding: "UTF-8",
    copies: 1,
  });

  // Join all receipt fragments into one string so QZ Tray sends a single
  // blob to the printer instead of N separate items — reduces print latency.
  const receiptBlob = buildEscPosReceipt(order, customerName, currencySymbol).join("");

  // logoBase64 is already fully-rendered ESC/POS raster bytes (GS v 0) from the server.
  // format:"command" + flavor:"base64" tells QZ Tray to decode base64 and forward raw bytes verbatim.
  const data: unknown[] = logoBase64
    ? [
        { type: "raw", format: "command", flavor: "base64", data: logoBase64 },
        receiptBlob,
      ]
    : [receiptBlob];

  await withTimeout(qz.print(config, data), 8000, "QZ print timeout");
};
